import React, { memo, useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useCharts } from './useCharts';
import { useComparisonLevel } from './useComparisonLevel';
import { useLoadingStore } from '../store/loadingStore';
import { calculateConfidenceInterval } from './utils';

const detectMinMax = (data: { emissions: { [x: string]: any }[] }[][]) => {
  let min = Infinity;
  let max = -Infinity;

  if (data.length === 0) {
    return { minValue: 0, maxValue: Infinity };
  } else {
    data.forEach((array: { emissions: { [x: string]: any }[] }[]) => {
      const allLogEmissionRates = array.flatMap(
        (result: { emissions: { [x: string]: any }[] }) =>
          result.emissions
            .map(
              (e: { [x: string]: any }) =>
                e['emission_rate_kgh'] || e['emissions'],
            )
            .filter((rate: number) => rate > 0),
      );

      if (allLogEmissionRates.length === 0) {
        return;
      }

      const minLogRateGlobal = Math.min(...allLogEmissionRates);
      const maxLogRateGlobal = Math.max(...allLogEmissionRates);

      const { upperBound } = calculateConfidenceInterval(allLogEmissionRates);

      if (minLogRateGlobal < min) {
        min = minLogRateGlobal;
      }
      if (Math.max(maxLogRateGlobal, upperBound) > max) {
        max = Math.max(maxLogRateGlobal, upperBound);
      }
    });

    return { minValue: min, maxValue: max };
  }
};
export const ComparisonLevel: React.FC<{
  period: string;
  time: string;
}> = memo(({ period, time }) => {
  const {
    allResults,
    chart1Results,
    chart2Results,
    chart3Results,
    totalResults,
    upstreamResults,
    midstreamResults,
  } = useComparisonLevel({
    period,
    time,
  });

  const { minValue, maxValue } = detectMinMax([
    allResults,
    chart1Results,
    chart2Results,
    chart3Results,
    upstreamResults,
    midstreamResults,
  ]);

  const { ChartCollection4, ChartCollection6 } = useCharts();
  const [activeTab] = useState(0);
  const setLoading = useLoadingStore((state) => state.setLoading);

  useEffect(() => {
    const isDataReady = () => {
      switch (activeTab) {
        case 0:
          return Object.entries(allResults).length > 0;
        case 1:
          return Object.entries(totalResults).length > 0;
        default:
          return false;
      }
    };

    if (isDataReady()) {
      setLoading(false, 100);
    }
  }, [activeTab, allResults, totalResults, setLoading]);

  const renderActiveTabContent = useCallback(() => {
    switch (activeTab) {
      case 0:
        return (
          <>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
              width={'100%'}
              padding={1}
            >
              <Grid
                item
                width={'48%'}
                paddingRight={'2%'}
                className={'allcharts withDivider'}
              >
                <div>
                  <ChartCollection6
                    allResults={allResults}
                    title={'AMI (All data)'}
                    logAxis={true}
                    compact={true}
                    min={minValue}
                    max={maxValue}
                  />
                  <ChartCollection6
                    allResults={chart1Results}
                    title={'Coal Sources'}
                    logAxis={true}
                    compact={true}
                    min={minValue}
                    max={maxValue}
                  />
                  <ChartCollection6
                    allResults={chart2Results}
                    title={'Concentrated Animal Feeding Operation'}
                    logAxis={true}
                    compact={true}
                    min={minValue}
                    max={maxValue}
                  />
                  <ChartCollection6
                    allResults={chart3Results}
                    title={'Landfill'}
                    logAxis={true}
                    compact={true}
                    min={minValue}
                    max={maxValue}
                  />
                  <ChartCollection6
                    allResults={upstreamResults}
                    title={'Upstream'}
                    logAxis={true}
                    compact={true}
                    min={minValue}
                    max={maxValue}
                  />
                  <ChartCollection6
                    allResults={midstreamResults}
                    title={'Midstream'}
                    logAxis={true}
                    compact={true}
                    min={minValue}
                    max={maxValue}
                  />
                </div>
              </Grid>
              <Grid
                item
                width={'48%'}
                paddingLeft={'2%'}
                className={'allcharts doubleSize'}
              >
                <ChartCollection4
                  compact={true}
                  type={'stacked'}
                  allResults={totalResults}
                />
              </Grid>
            </Grid>
          </>
        );
      default:
        return null;
    }
  }, [
    activeTab,
    allResults,
    chart1Results,
    chart2Results,
    chart3Results,
    totalResults,
  ]);

  return (
    <div className="tabs" style={{ marginTop: '125px', padding: '0 10px' }}>
      <div className="tab-content">{renderActiveTabContent()}</div>
    </div>
  );
});
